"use client";

import { Locale, UpdateAction } from "@/enums/common.enum";
import {
  PropertyListingType,
  PropertyListingViewType,
  PropertyPurposeType,
  PropertyRentalPeriodType,
  PropertyType,
} from "@/enums/property.enum";
import { usePathname } from "@/i18n/routing";
import { PropertySearchPageUrlSearchParams } from "@/interfaces/api.interface";
import { Location, SearchLocation } from "@/interfaces/property.interface";
import {
  convertStateToListingUrl,
  convertUrlToState,
} from "@/lib/property/listing/helpers";
import { convertSearchParamsToObject } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import React, { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_CONTEXT_VALUE,
  DEFAULT_SEARCH_STATE,
  INITIAL_PAGE,
} from "./constants";
import { PropertyListingSearchReferrer } from "./enums";
import { SearchContextProps, SearchState, UpdateOptions } from "./interfaces";

export const SearchContext = React.createContext<SearchContextProps>(
  DEFAULT_CONTEXT_VALUE
);

export const SearchProvider = ({
  children,
  initialFilters,
  initialSearchParams,
}: {
  children: React.ReactNode;
  initialFilters?: Partial<SearchState>;
  initialSearchParams?: PropertySearchPageUrlSearchParams;
}) => {
  const pathname = usePathname();
  const locale = useLocale() as Locale;
  const queryClient = useQueryClient();

  const [isInitialized, setIsInitialized] = useState(false);
  const [defaultUpdateOptions, setDefaultUpdateOptions] =
    useState<UpdateOptions>({
      updateMode: "direct",
    });

  // doing this because when we update the window location by doing .pushState, the search params won't be loaded, need to set it again
  const [searchParams, setSearchParams] = useState(() =>
    typeof window === "undefined"
      ? initialSearchParams!
      : new URLSearchParams(window.location.search)
  );
  // https://react.dev/reference/react/useState#avoiding-recreating-the-initial-state
  const [state, setState] = useState<SearchState>(() => ({
    ...DEFAULT_SEARCH_STATE,
    ...initialFilters,
  }));

  const {
    bathrooms,
    bedrooms,
    price,
    area,
    amenities,
    pageParams,
    coordinates,
    rentalPeriod,
    furnishing,
    listing: { property, type },
    locations,
    purpose,
    sort,
    view,
    tour,
    badges,
    agencyId,
    referrer,
  } = state;

  const updateState = (
    updates: Partial<SearchState>,
    options: UpdateOptions = defaultUpdateOptions
  ) => {
    if (options.updateMode === "direct") {
      setState((prev) => ({ ...prev, ...updates, pageParams: INITIAL_PAGE }));
    } else {
      updateURL(updates);
    }
  };

  const updateURL = (updates: Partial<SearchState>) => {
    const url = convertStateToListingUrl(state, updates);

    window.history.pushState({}, "", `/${locale}${url}`);
    setSearchParams(new URLSearchParams(window.location.search));
  };

  const updateLocations = (
    location: SearchLocation | null,
    action: UpdateAction,
    options: UpdateOptions = defaultUpdateOptions
  ) => {
    const getNewLocations = (prevLocations: SearchLocation[] | null) => {
      if (action === UpdateAction.ADD) {
        if (prevLocations) {
          return [...prevLocations, location!];
        }
        return [location!];
      } else if (action === UpdateAction.REMOVE) {
        return prevLocations?.filter((loc) => loc.value !== location?.value);
      } else if (action == UpdateAction.CLEAR) {
        return [];
      }
    };

    const newLocations = getNewLocations(locations) ?? [];

    queryClient.setQueryData(
      [
        "PROPERTIES",
        "LOCATION",
        "SLUGS",
        { ...newLocations.map((location) => location.fullSlug) },
        locale,
      ],
      newLocations
    );

    if (options.updateMode === "direct") {
      updateState({
        locations: newLocations,
      });
    } else {
      updateURL({
        locations: newLocations,
        pageParams: INITIAL_PAGE,
      });
    }
  };

  const handleToggle = useCallback(
    <T,>(
      key: keyof SearchState,
      value: T,
      options: UpdateOptions = defaultUpdateOptions
    ) => {
      const getUpdatedValues = (prevValues: T[] | null) => {
        if (prevValues) {
          if (prevValues.includes(value)) {
            const filtered = prevValues.filter((_value) => _value !== value);

            return filtered.length > 0 ? filtered : null;
          }

          return [...prevValues, value];
        }

        return [value];
      };

      if (options.updateMode === "direct") {
        updateState({
          [key]: getUpdatedValues(state[key] as Array<T>),
        });
      } else {
        updateURL({
          [key]: getUpdatedValues(state[key] as Array<T>),
          pageParams: INITIAL_PAGE,
        });
      }
    },
    [JSON.stringify(state), defaultUpdateOptions]
  );

  const handleSearch = () => {
    const url = convertStateToListingUrl(state);

    window.location.href = `/${locale}/${url}&referrer=${PropertyListingSearchReferrer.HOMEPAGE}`;
  };

  useEffect(() => {
    setDefaultUpdateOptions({
      updateMode: pathname === "/" ? "direct" : "url",
    });
  }, [pathname]);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // const cachedLocations = useMemo(() => {
  //   return queryClient.getQueryData([
  //     "PROPERTIES",
  //     "LOCATION",
  //     "SLUGS",
  //     { ...locations.map((location) => location.fullSlug) },
  //     locale,
  //   ]) as Location[];
  // }, [JSON.stringify(locations)]);

  useEffect(() => {
    if (pathname.startsWith("/search")) {
      const _searchParams = convertSearchParamsToObject(
        searchParams as URLSearchParams
      ) as PropertySearchPageUrlSearchParams;

      const cachedLocations = queryClient.getQueryData([
        "PROPERTIES",
        "LOCATION",
        "SLUGS",
        { ..._searchParams.locations?.split(",") },
        locale,
      ]) as Location[];

      const state = convertUrlToState({
        searchParams: _searchParams,
        locations: cachedLocations,
      }) as SearchState;

      setState((prev) => ({
        ...prev,
        ...state,
      }));
    }
  }, [pathname, searchParams?.toString(), locale, queryClient]);

  return (
    <SearchContext.Provider
      value={{
        referrer,
        view,
        isInitialized,
        bathrooms,
        bedrooms,
        price,
        area,
        amenities,
        pageParams,
        coordinates,
        rentalPeriod,
        furnishing,
        listing: { property, type },
        locations,
        purpose,
        sort,
        tour,
        badges,
        agencyId,
        updateCoordinates: (coordinates, options = defaultUpdateOptions) => {
          updateState({ coordinates }, options);
        },
        updateView: (type, options = defaultUpdateOptions) => {
          if (type === PropertyListingViewType.LIST) {
            updateState({ view: type, coordinates: null }, options);
          } else {
            updateState({ view: type }, options);
          }
        },
        updateListing: (
          {
            type,
            property = null,
          }: { type: PropertyListingType; property?: PropertyType | null },
          options = defaultUpdateOptions
        ) => {
          if (
            type == PropertyListingType.BUY ||
            type == PropertyListingType.RENT ||
            type == PropertyListingType.OFF_PLAN
          ) {
            return updateState(
              {
                listing: { type, property },
                purpose: PropertyPurposeType.RESIDENTIAL,
              },
              options
            );
          }
          return updateState(
            {
              listing: { type, property },
              purpose: PropertyPurposeType.COMMERCIAL,
            },
            options
          );
        },
        updateLocations,
        updatePageParams: (newParams, options = defaultUpdateOptions) =>
          updateState({ pageParams: { ...pageParams, ...newParams } }, options),
        toggleBathroom: (value) =>
          handleToggle("bathrooms", value, defaultUpdateOptions),
        toggleBedroom: (value) =>
          handleToggle("bedrooms", value, defaultUpdateOptions),
        updatePrice: (newPrice, options = defaultUpdateOptions) =>
          updateState(
            {
              price: { ...price, ...newPrice },
            },
            options
          ),
        updateArea: (newArea, options = defaultUpdateOptions) =>
          updateState(
            {
              area: { ...area, ...newArea },
            },
            options
          ),
        toggleAmenity: (value) =>
          handleToggle("amenities", value, defaultUpdateOptions),

        toggleBadge: (value) =>
          handleToggle("badges", value, defaultUpdateOptions),
        updateBadge: (newBadges, options = defaultUpdateOptions) => {
          updateState(
            {
              badges: newBadges,
            },
            options
          );
        },

        updateAmenity: (newAmenities, options = defaultUpdateOptions) => {
          updateState(
            {
              amenities: newAmenities,
            },
            options
          );
        },
        updateRentalPeriod: (rentalPeriod, options = defaultUpdateOptions) => {
          updateState({ rentalPeriod }, options);
        },
        updateTour: (tour, options = defaultUpdateOptions) => {
          updateState({ tour }, options);
        },

        updatePurpose: (purpose, options = defaultUpdateOptions) =>
          updateState(
            {
              purpose,
              ...(purpose === PropertyPurposeType.COMMERCIAL && {
                bedrooms: null,
                bathrooms: null,
                amenities: null,
                rentalPeriod: PropertyRentalPeriodType.YEARLY,
                propertyType: null,
              }),
            },
            options
          ),
        updateSort: (sort, options = defaultUpdateOptions) =>
          updateState({ sort }, options),
        updateFurnishing: (furnishing, options = defaultUpdateOptions) => {
          updateState(
            {
              furnishing: furnishing,
            },
            options
          );
        },
        clearFilter: (options = defaultUpdateOptions) =>
          updateState(
            {
              listing: {
                property: null,
                type: PropertyListingType.BUY,
              },
              price: { from: null, to: null },
              bedrooms: null,
              bathrooms: null,
              amenities: null,
              rentalPeriod: PropertyRentalPeriodType.YEARLY,
            },
            options
          ),
        handleSearch,
        setState,
        state,
        // use updateState when a lot of updates needs to be simultaneously
        updateState,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  return React.useContext(SearchContext);
};
